import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Typography,
  IconButton,
  Box,
  InputAdornment
} from '@mui/material';
import { 
  Mail, 
  User, 
  Phone, 
  Shield,
  Building,
  MapPin,
  Globe,
  Edit,
  Plus,
  X,
  Eye,
  EyeOff
} from 'lucide-react';
import Button from '../common/Button';
import { baseRoleOptions } from '../../utils/adminDropdownOptions';
import { getFullName } from '../../utils/helpers';

const UserManagementModal = ({
  open,
  onClose,
  onSubmit,
  initialData = null,
  isLoading = false,
  title = "Create User",
  submitButtonText = "Create User",
  errors,
  setErrors
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    full_name: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    role: 'professional',
    is_active: true,
    // is_email_verified: false,
  });

  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        email: initialData.email || '',
        password: '',
        password_confirm: '',
        full_name: initialData.full_name || getFullName(initialData.first_name, initialData.last_name) || '', 
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        phone_number: initialData.phone_number || '',
        role: initialData.role || 'professional',
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
        // is_email_verified: initialData.is_email_verified || false,
      });
    } else {
      setFormData({
        email: '',
        password: '',
        password_confirm: '',
        full_name: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        role: 'professional',
        is_active: true,
        // is_email_verified: false,
      });
    }
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [initialData, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear confirm password error when either password field changes
    if ((field === 'password' || field === 'password_confirm') && errors.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: ''
      }));
    }
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(prev => !prev);
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Password validation (only required for new users)
    if (!initialData) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      // Confirm password validation
      if (!formData.password_confirm) {
        newErrors.password_confirm = 'Please confirm your password';
      } else if (formData.password !== formData.password_confirm) {
        newErrors.password_confirm = 'Passwords do not match';
      }
    }

    // For existing users, validate password only if provided
    if (initialData && formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (initialData && formData.password && !formData.password_confirm) {
      newErrors.password_confirm = 'Please confirm your password';
    } else if (initialData && formData.password && formData.password !== formData.password_confirm) {
      newErrors.password_confirm = 'Passwords do not match';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (formData.phone_number && !/^\+?[\d\s-()]+$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Phone number format is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Prepare data for submission
      const submitData = { ...formData };
      
      // Remove password and confirmPassword fields if empty (for updates)
      if (initialData) {
        if (!submitData.password) {
          delete submitData.password;
        }
        if(!submitData.password_confirm)
            delete submitData.password_confirm;
      }
      
      onSubmit(submitData);
    }
  };

  const handleClose = () => {
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <form onSubmit={handleSubmit} className='sentry-mask'>
        <DialogTitle className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {initialData ? (
                <Edit className="w-6 h-6 text-blue-600" />
              ) : (
                <Plus className="w-6 h-6 text-green-600" />
              )}
              <Typography variant="h6" className="text-secondary-900">
                {title}
              </Typography>
            </div>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: 'gray' }}
            >
              <X />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className="pt-6">
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid size={{xs: 12}}>
              <Typography variant="subtitle1" className="font-semibold mb-3 text-secondary-900">
                Basic Information
              </Typography>
            </Grid>

            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                disabled={initialData !== null}
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    ),
                  }
                }}
                placeholder="user@example.com"
              />
            </Grid>

            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={handleChange('full_name')}
                error={!!errors.full_name}
                helperText={errors.full_name}
                slotProps={{
                  input: {
                    startAdornment: (
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                    ),
                  }
                }}
                placeholder="John Doe"
              />
            </Grid>

            {(!initialData) && (
              <>
                <Grid size={{xs: 12, md: 6}}>
                  <TextField
                    fullWidth
                    label={initialData ? "New Password" : "Password"}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange('password')}
                    error={!!errors.password}
                    helperText={errors.password || (initialData ? "Leave blank to keep current password" : "")}
                    required={!initialData}
                    placeholder={initialData ? "Enter new password" : "Enter password"}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={togglePasswordVisibility}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    }}
                  />
                </Grid>

                <Grid size={{xs: 12, md: 6}}>
                  <TextField
                    fullWidth
                    label={initialData ? "Confirm New Password" : "Confirm Password"}
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.password_confirm}
                    onChange={handleChange('password_confirm')}
                    error={!!errors.password_confirm}
                    helperText={errors.password_confirm}
                    required={!initialData}
                    placeholder={initialData ? "Confirm new password" : "Confirm password"}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={toggleConfirmPasswordVisibility}
                              edge="end"
                              size="small"
                            >
                              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    }}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
              <FormControl fullWidth required error={!!errors.role}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={handleChange('role')}
                >
                  {baseRoleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role && (
                  <Typography variant="caption" color="error">
                    {errors.role}
                  </Typography>
                )}
              </FormControl>
            </Grid>
              </>
            )}

            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                onChange={handleChange('phone_number')}
                error={!!errors.phone_number}
                helperText={errors.phone_number}
                slotProps={{
                  input: {
                    startAdornment: (
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                    ),
                  }
                }}
                placeholder="+254734567890"
              />
            </Grid>

            

            {/* Account Status */}
            <Grid size={{xs: 12}}>
              <Typography variant="subtitle1" className="font-semibold mb-3 text-secondary-900">
                Account Status
              </Typography>
            </Grid>

            <Grid size={{xs: 12, md: 6}}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange('is_active')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography className="font-medium">Active Account</Typography>
                    <Typography variant="body2" className="text-gray-600">
                      {formData.is_active ? 'User can access their account' : 'User account is deactivated'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

                {/* {!initialData && (

            <Grid size={{xs: 12, md: 6}}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_email_verified}
                    onChange={handleSwitchChange('is_email_verified')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography className="font-medium">Email Verified</Typography>
                    <Typography variant="body2" className="text-gray-600">
                      {formData.is_email_verified ? 'Email is verified' : 'Email requires verification'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
                )} */}
          </Grid>
        </DialogContent>

        <DialogActions className="border-t p-4">
          <div className="flex space-x-3 w-full justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
            >
              {submitButtonText}
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserManagementModal;